import { component } from "picoapp";
import {
  getQueryStringParameter,
  getAllQueryStringParameters,
  setQueryStringParameter,
} from "../../../scripts/lib/utils";
import { findSelectedVariants, getVariantMetadata } from "../../utilities";
import {
  type Context,
  type SelectedVariant,
  type SelectedOption,
} from "../../state";
import { isCustomerEligibleForGwp } from "../../utilities/customer";
import { TablescapeContext, TablescapeEvent } from "@tablescape/context";
import { PageModal } from "@tablescape/state";
import {
  type LanguageCode,
  type CountryCode,
} from "@shopify/hydrogen-react/storefront-api-types";

// Type guard functions
const isValidCountryCode = (
  value: string | undefined
): value is CountryCode => {
  return typeof value === "string" && value.length > 0;
};

const isValidLanguageCode = (
  value: string | undefined
): value is LanguageCode => {
  return typeof value === "string" && value.length > 0;
};

const getCountryAndLanguage = () => {
  const countryCode = document.documentElement.dataset?.countryCode;
  const languageCode = document.documentElement.dataset?.languageCode;

  if (!isValidCountryCode(countryCode) || !isValidLanguageCode(languageCode)) {
    console.warn(
      "Missing or invalid country/language code in document dataset"
    );
    return null;
  }

  return {
    country: countryCode,
    language: languageCode,
  };
};

const hydrateProductVariantMetadata = (
  context: Context,
  productHandle: string,
  {
    selectedOptions,
    selectedVariant,
  }: {
    selectedOptions: string[];
    selectedVariant: SelectedVariant;
  }
) => {
  if (!selectedVariant) {
    return;
  }

  const { products, ...rest } = context.getState();
  const updatedProducts = {
    ...products,
    [productHandle]: {
      ...(products?.[productHandle] || {}),
      selectedVariant,
    },
  };

  context.hydrate({
    ...rest,
    products: updatedProducts,
    selectedOptions,
  });

  context.emit(TablescapeEvent.VARIANT_METADATA_LOADED);

  TablescapeContext.emit(TablescapeEvent.VARIANT_SELECTED, {
    products: updatedProducts,
    selectedVariant,
  });
};

const abortController = new AbortController();

const getSelectedOptions = ({ node }: { node: HTMLElement }) => {
  const fieldsets: HTMLFieldSetElement[] = Array.from(
    node.querySelectorAll("fieldset")
  );
  return fieldsets
    .map((fieldset) => {
      return Array.from(fieldset.querySelectorAll("input")).find(
        (radio) => radio.checked
      )?.value;
    })
    .filter((value): value is string => value !== undefined);
};

const updateOptions = ({ node }: { node: HTMLElement }) => {
  const selectedOptions = getSelectedOptions({ node });
  return selectedOptions;
};

const updateVariantQueryString = ({ context }: { context: Context }) => {
  const { products } = context.getState();
  const selectedVariants = findSelectedVariants(products || {});

  if (!selectedVariants?.length) {
    return;
  }

  setQueryStringParameter("variant", selectedVariants[0].id);
};

const updateVariant = ({
  context,
  node,
  productHandle,
  selectedVariant,
}: {
  context: Context;
  node: HTMLElement;
  productHandle: string;
  selectedVariant: SelectedVariant;
}) => {
  const selectedOptions = updateOptions({ node });

  hydrateProductVariantMetadata(context, productHandle, {
    selectedVariant,
    selectedOptions,
  });

  updateVariantQueryString({ context });
};

const resetUnrelatedIndexes = ({
  form,
  isKittedProduct,
}: {
  form: HTMLElement;
  isKittedProduct: boolean;
}) => {
  const deselectedTargets = form.querySelectorAll<HTMLSelectElement>(
    isKittedProduct
      ? `select:not([data-kitted-product])`
      : `select[data-kitted-product]`
  );

  deselectedTargets.forEach((sibling) => {
    sibling.selectedIndex = -1;
  });
};

const updateSelectedOption = ({
  input,
  variantId,
}: {
  input: HTMLElement;
  variantId: string;
}) => {
  const form = input.closest("form");

  if (!form || !input.parentElement) {
    return;
  }

  const select = form.querySelector<HTMLSelectElement>(
    `select[data-product-handle=${input.parentElement.dataset.productHandle}]`
  );

  if (select) {
    const selectedIndex = Array.from(select.options).findIndex(
      (option) => option.value === variantId
    );

    select.selectedIndex = selectedIndex;
  }

  const isKittedProduct = select?.dataset.kittedProduct === "true";
  resetUnrelatedIndexes({ form, isKittedProduct });
};

const updateAutoAddedGwpSelectedOptions = ({
  input,
  selectedVariant,
}: {
  input: HTMLElement;
  selectedVariant: SelectedVariant;
}) => {
  const form = input.closest("form");

  if (!form) {
    return;
  }

  const select = form.querySelector<HTMLSelectElement>(
    'select[name="variant-options"]'
  );

  if (!select) {
    return;
  }
  const giftWithPurchase = selectedVariant.giftWithPurchase;

  if (
    giftWithPurchase &&
    isCustomerEligibleForGwp(giftWithPurchase) &&
    giftWithPurchase.autoAddVariants?.length
  ) {
    select.dataset.autoAddVariantIds = giftWithPurchase.autoAddVariants
      .map((variant) => variant.id)
      .join(",");
    select.dataset.autoAddVariantHasShippingNotes =
      giftWithPurchase.autoAddVariants
        .map((variant) => (variant.shippingNote ? "true" : "false"))
        .join(",");
  } else {
    delete select.dataset.autoAddVariantIds;
    delete select.dataset.autoAddVariantHasShippingNotes;
  }
};

const updateSelectedOptionValue = ({
  input,
  node,
}: {
  input: HTMLInputElement;
  node: HTMLElement;
}) => {
  const hasOptionCategories =
    node.querySelectorAll<HTMLElement>("[data-option-category]").length > 0;
  let optionTextNode;
  if (hasOptionCategories) {
    const currentOptionCategory = input.closest<HTMLElement>(
      "[data-option-category]"
    )?.dataset.optionCategory;
    optionTextNode = node.querySelector<HTMLElement>(
      `[data-option-category='${currentOptionCategory}'] [data-selected-option-value] [data-tbsp-component='text']`
    );
    const otherOptionTextNodes = node.querySelectorAll<HTMLElement>(
      `[data-option-category]:not([data-option-category='${currentOptionCategory}']) [data-selected-option-value] [data-tbsp-component='text']`
    );
    otherOptionTextNodes.forEach((node) => (node.innerHTML = "&nbsp;"));
  } else {
    optionTextNode = input
      .closest<HTMLFieldSetElement>("fieldset")
      ?.querySelector<HTMLElement>(
        "[data-selected-option-value] [data-tbsp-component='text']"
      );
  }
  if (optionTextNode) {
    optionTextNode.innerHTML = input.value;
  }
};

const updateKlarna = ({
  isProductBundle,
  selectedVariant,
}: {
  isProductBundle: boolean;
  selectedVariant: SelectedVariant;
}) => {
  if (isProductBundle || !("Klarna" in window)) {
    return;
  }

  document
    .getElementsByTagName("klarna-placement")[0]
    ?.setAttribute(
      "data-purchase-amount",
      String(Math.round(selectedVariant.price))
    );

  window.Klarna?.OnsiteMessaging?.refresh();
};

const getVariant = async (
  {
    bundleProductImageUrl,
    country,
    isKittedProduct,
    isMainProduct,
    isSupplementalProduct,
    language,
    productHandle,
    selectedOptions,
    variantId,
  }: {
    bundleProductImageUrl: string | undefined;
    country: CountryCode;
    isKittedProduct: boolean;
    isMainProduct: boolean;
    isSupplementalProduct: boolean;
    language: LanguageCode;
    productHandle: string;
    selectedOptions: SelectedOption[];
    variantId: string;
  },
  {
    context,
    fieldset,
    input,
  }: {
    context?: Context;
    fieldset?: HTMLFieldSetElement;
    input?: HTMLInputElement;
  } = {}
) => {
  try {
    const selectedVariant = await getVariantMetadata(
      {
        country,
        language,
        productHandle,
        selectedOptions,
        variantId,
      },
      {
        abortController,
        loader: () => {
          if (context && fieldset && input) {
            context.emit("tbsp:variant-metadata-loading", {
              fieldset,
              input,
              isKittedProduct,
              isMainProduct,
              isSupplementalProduct,
            });
          }
        },
      }
    );

    if (!selectedVariant) {
      return undefined;
    }

    return {
      ...selectedVariant,
      media: bundleProductImageUrl
        ? [
            {
              ...selectedVariant.media[0],
              image: {
                ...(selectedVariant.media[0] &&
                "image" in selectedVariant.media[0]
                  ? selectedVariant.media[0].image
                  : {}),
                url: bundleProductImageUrl,
              },
            },
            ...selectedVariant.media.slice(1),
          ]
        : selectedVariant.media,
      kittedProduct: isKittedProduct,
      mainProduct: isMainProduct,
      supplementalProduct: isSupplementalProduct,
    };
  } catch (e) {
    console.error(e);
  }
};

const getBundleProductImageUrl = (
  node: HTMLElement,
  input: HTMLInputElement
) => {
  return node.querySelector<HTMLSelectElement>(
    `select[name="shopify-bundle-metadata"] optgroup[label="${input
      ?.closest<HTMLElement>("[data-product-option-name]")
      ?.dataset?.productOptionName?.replace(/ \(.*\)/, "")}"] option[value="${
      input.value
    }"]`
  )?.dataset?.variantImage;
};

enum ATTRIBUTES {
  DATA_KITTED_PRODUCT = "data-kitted-product",
  DATA_PREVIOUS_CHECKED = "data-previous-checked",
  DATA_PRODUCT_HANDLE = "data-product-handle",
}

const clearProductContext = (
  context: Context,
  selectedVariantIds: string[]
) => {
  const currentState = context.getState();

  if (!currentState.products) {
    return;
  }

  const clearedProducts = {
    ...currentState.products,
  };

  selectedVariantIds.forEach((id) => {
    if (clearedProducts[id]) {
      delete clearedProducts[id];
    }
  });

  TablescapeContext.emit(TablescapeEvent.PRODUCT_METADATA_CLEARED, {
    products: clearedProducts,
  });

  context.hydrate({
    ...currentState,
    products: clearedProducts,
  });
};

const handleInputChange = async ({
  context,
  input,
  isProductBundle,
  isShopifyBundle,
  node,
}: {
  context: Context;
  input: HTMLInputElement;
  isProductBundle: boolean;
  isShopifyBundle: boolean;
  isVariantBundle: boolean;
  node: HTMLElement;
}) => {
  const localeData = getCountryAndLanguage();
  if (!localeData) {
    return;
  }

  const { country, language } = localeData;

  const fieldset = input.closest<HTMLFieldSetElement>(
    `fieldset[${ATTRIBUTES.DATA_PRODUCT_HANDLE}]`
  );

  if (!fieldset || !fieldset.parentElement) {
    return;
  }

  const select = fieldset.parentElement.querySelector<HTMLSelectElement>(
    `select[${ATTRIBUTES.DATA_PRODUCT_HANDLE}]`
  );
  const productHandle = select?.dataset.productHandle;
  const isMainProduct =
    fieldset.dataset.supplementalProduct === "false" &&
    (isProductBundle || isShopifyBundle);
  const isKittedProduct = fieldset.dataset.kittedProduct === "true";
  const isSupplementalProduct =
    fieldset.dataset.supplementalProduct === "true" && !isKittedProduct;

  // Clear previously checked inputs
  const immediatePreviousCheckedInput = fieldset?.querySelector(
    `[${ATTRIBUTES.DATA_PREVIOUS_CHECKED}]`
  );

  immediatePreviousCheckedInput?.removeAttribute(
    ATTRIBUTES.DATA_PREVIOUS_CHECKED
  );

  if (isKittedProduct) {
    const explodedInputs = document.querySelectorAll<HTMLInputElement>(
      `fieldset input[type='radio'][checked]`
    );
    const explodedSelects = document.querySelectorAll<HTMLSelectElement>(
      `select:not([${ATTRIBUTES.DATA_KITTED_PRODUCT}])`
    );

    clearProductContext(
      context,
      Array.from(explodedSelects)
        .map((select) => select.dataset.productHandle)
        .filter((handle): handle is string => handle !== undefined)
    );

    explodedInputs.forEach((input) => {
      input.setAttribute(ATTRIBUTES.DATA_PREVIOUS_CHECKED, "true");
      input.removeAttribute("checked");

      input.checked = false;
    });

    explodedSelects.forEach((select) => {
      select.selectedIndex = -1;
    });
  } else {
    const kittedProductInput = document.querySelector<HTMLInputElement>(
      `fieldset[${ATTRIBUTES.DATA_KITTED_PRODUCT}] input[type='radio'][checked]`
    );
    const kittedProductSelect = document.querySelector<HTMLSelectElement>(
      `select[${ATTRIBUTES.DATA_KITTED_PRODUCT}]`
    );

    const previouslyCheckedInputs = document.querySelectorAll<HTMLInputElement>(
      `fieldset:not([${ATTRIBUTES.DATA_KITTED_PRODUCT}]):not([${ATTRIBUTES.DATA_PRODUCT_HANDLE}='${productHandle}']) input[${ATTRIBUTES.DATA_PREVIOUS_CHECKED}]`
    );

    if (kittedProductInput) {
      kittedProductInput.removeAttribute("checked");

      kittedProductInput.checked = false;
    }

    if (kittedProductSelect) {
      clearProductContext(
        context,
        [kittedProductSelect]
          .map((select) => select.dataset.productHandle)
          .filter((handle): handle is string => handle !== undefined)
      );

      kittedProductSelect.selectedIndex = -1;
    }

    previouslyCheckedInputs.forEach((input) => {
      input.removeAttribute(ATTRIBUTES.DATA_PREVIOUS_CHECKED);
      input.setAttribute("checked", "checked");
      input.click();
    });

    const prevCheckedInput = fieldset.querySelector("input[checked]");
    prevCheckedInput?.removeAttribute("checked");
  }

  input.setAttribute("checked", "checked");

  const checkedInputs = document.querySelectorAll(
    `fieldset[${ATTRIBUTES.DATA_PRODUCT_HANDLE}='${productHandle}'] input[type='radio'][checked]`
  );

  const selectedOptions = Array.from(checkedInputs)
    .filter(
      (input): input is HTMLInputElement => input instanceof HTMLInputElement
    )
    .map((input) => ({
      name: input.name.replace(`${productHandle}:`, ""),
      value: input.value,
    }));

  const selectedProductVariantHandle = Array.from(checkedInputs)
    .filter(
      (input): input is HTMLInputElement => input instanceof HTMLInputElement
    )
    .map((input) => input.parentElement?.dataset.productVariantHandle)
    .filter((handle): handle is string => handle !== undefined)
    .join("-");

  if (!productHandle || !selectedOptions.length || !select) {
    return;
  }

  const variantOption = select.querySelector<HTMLOptionElement>(
    `option[data-product-variant-handle='${selectedProductVariantHandle}']`
  );

  if (!variantOption) {
    return;
  }

  const variantId = variantOption.value;

  const bundleProductImageUrl = getBundleProductImageUrl(node, input);

  const selectedVariant = await getVariant(
    {
      country,
      language,
      bundleProductImageUrl,
      isKittedProduct,
      isMainProduct,
      isSupplementalProduct,
      productHandle,
      selectedOptions,
      variantId,
    },
    { context, fieldset, input }
  );

  if (selectedVariant === undefined) {
    context.emit("tbsp:variant-metadata-load-error");

    const currentVariantQuery = getQueryStringParameter("variant");

    if (currentVariantQuery !== variantId) {
      setQueryStringParameter("variant", variantId);

      window.location.reload();
    }

    return;
  }

  updateVariant({
    context,
    node,
    productHandle,
    selectedVariant,
  });
  updateSelectedOption({ input, variantId });
  updateAutoAddedGwpSelectedOptions({ input, selectedVariant });
  updateSelectedOptionValue({ node, input });
  updateKlarna({ isProductBundle, selectedVariant });
};

const updateAndCacheSelectedVariants = ({
  context,
  isProductBundle,
  isShopifyBundle,
  isVariantBundle,
  node,
}: {
  context: Context;
  isProductBundle: boolean;
  isShopifyBundle: boolean;
  isVariantBundle: boolean;
  node: HTMLElement;
}) => {
  const localeData = getCountryAndLanguage();
  if (!localeData) {
    return;
  }

  const { country, language } = localeData;

  const variantsByQuery = getAllQueryStringParameters("variant");

  variantsByQuery.forEach((variantId) => {
    const input = node.querySelector<HTMLInputElement>(
      `fieldset [data-product-variant-id="${variantId}"] input`
    );

    if (!input) {
      updateVariantQueryString({ context });
      return;
    }

    updateSelectedOption({ input, variantId });

    if (input) {
      input.click();
      handleInputChange({
        node,
        context,
        input,
        isProductBundle,
        isShopifyBundle,
        isVariantBundle,
      });
    }
  });

  const variantOptions = [
    ...node.querySelectorAll<HTMLSelectElement>(
      `select[name="variant-options"]`
    ),
  ];

  const isKittedProduct = variantOptions.every(
    (select) => select.dataset.kittedProduct === "true"
  );

  resetUnrelatedIndexes({
    form: node,
    isKittedProduct,
  });

  variantOptions.forEach(async (select) => {
    if (select.selectedOptions.length === 0) {
      return;
    }

    const currentVariantId = select.selectedOptions[0].value;
    const checkedInputs = document.querySelectorAll(
      "input[type='radio'][checked]"
    );
    const productHandle = select.dataset.productHandle;

    if (!productHandle) {
      return;
    }

    const currentSelectedOptions = Array.from(checkedInputs)
      .filter(
        (input): input is HTMLInputElement => input instanceof HTMLInputElement
      )
      .filter((input) => input.name.startsWith(`${productHandle}:`))
      .map((input) => ({
        name: input.name.replace(`${productHandle}:`, ""),
        value: input.value,
      }));

    if (!variantsByQuery.includes(currentVariantId)) {
      const inputElement =
        node.querySelector<HTMLInputElement>("fieldset input");
      const bundleProductImageUrl = inputElement
        ? getBundleProductImageUrl(node, inputElement)
        : undefined;

      const selectedVariant = await getVariant({
        country,
        language,
        bundleProductImageUrl,
        isKittedProduct,
        isMainProduct:
          select.dataset.supplementalProduct === "false" && isProductBundle,
        isSupplementalProduct:
          select.dataset.supplementalProduct === "true" && !isKittedProduct,
        productHandle,
        variantId: currentVariantId,
        selectedOptions: currentSelectedOptions,
      });

      const selectedOptions = updateOptions({
        node,
      });

      if (selectedVariant) {
        hydrateProductVariantMetadata(context, productHandle, {
          selectedVariant,
          selectedOptions,
        });
      }
    }
  });
};

export const productForm = component<Context>((node, context) => {
  const variantSelect = node.querySelector<HTMLSelectElement>(
    'select[name="variant-options"]'
  );
  const isProductBundle = node.dataset.productBundle === "true";
  const isShopifyBundle = node.dataset.shopifyBundle === "true";
  const isVariantBundle = node.dataset.variantBundle === "true";

  if (!variantSelect) {
    return;
  }

  updateAndCacheSelectedVariants({
    context,
    isProductBundle,
    isShopifyBundle,
    isVariantBundle,
    node,
  });

  node.addEventListener("change", async (e: Event) => {
    if (!(e.target instanceof HTMLInputElement)) {
      return;
    }
    const input = e.target;

    handleInputChange({
      context,
      input,
      isProductBundle,
      isShopifyBundle,
      isVariantBundle,
      node,
    });
  });
});

export const modalToggle = component<Context>((node) => {
  node.addEventListener("click", (e: Event) => {
    e.preventDefault();
    TablescapeContext.emit(TablescapeEvent.TOGGLE_PAGE_MODAL, {
      ui: {
        pageModalTarget: PageModal.SIZE_GUIDE_MODAL,
      },
    });
  });
});
